import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../utils/operations';
import { refreshMediaOverrides, useMediaOverrides } from '../../utils/media';
import { PACKAGES } from '../../data/packages';
import { DESTINATIONS } from '../../data/destinations';

const HERO_SLIDES = [
  { slot: 'hero:0', name: 'Hero — Slide 1 (São Tomé)', img: '/media/4923818b-7917-45ff-9853-4bb847436818.jpeg' },
  { slot: 'hero:1', name: 'Hero — Slide 2 (Príncipe)', img: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?q=80&w=1600&auto=format&fit=crop' },
  { slot: 'hero:2', name: 'Hero — Slide 3 (Cabo Verde)', img: '/media/56467cf4-1c33-4530-bae3-1167dc9c64fa.jpeg' },
];

const STORY_SLOTS = [
  { slot: 'story:0', name: 'Histórias de destino — 1 (Planear)', img: '/media/4923818b-7917-45ff-9853-4bb847436818.jpeg' },
  { slot: 'story:1', name: 'Histórias de destino — 2 (Cabo Verde)', img: '/media/56467cf4-1c33-4530-bae3-1167dc9c64fa.jpeg' },
  { slot: 'story:2', name: 'Histórias de destino — 3 (Príncipe)', img: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?q=80&w=900&auto=format&fit=crop' },
];

const GROUPS = [
  { id: 'hero', label: 'Homepage', slots: [...HERO_SLIDES, ...STORY_SLOTS] },
  { id: 'pkg', label: 'Pacotes Completos', slots: PACKAGES.map((p) => ({ slot: `pkg:${p.slug}`, name: p.name, img: p.img })) },
  {
    id: 'dst',
    label: 'Destinos',
    slots: DESTINATIONS.flatMap((d) => [
      { slot: `dst:${d.slug}:card`, name: `${d.name} — Cartão`, img: d.img },
      { slot: `dst:${d.slug}:hero`, name: `${d.name} — Página (hero)`, img: d.hero || d.img },
      ...(d.subs || []).map((s) => ({ slot: `dst:${d.slug}:${s.slug}`, name: `${d.name} — ${s.name}`, img: s.img })),
    ]),
  },
];

const MediaSlotCard = ({ slot, name, defaultImg, overrides, onSaved, onMoveLeft, onMoveRight }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const current = overrides[slot] || defaultImg;
  const isCustom = Boolean(overrides[slot]);

  const save = async (imageUrl) => {
    setBusy(true);
    try {
      await apiFetch(`/media/overrides/${slot}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: imageUrl }) });
      await refreshMediaOverrides();
      toast.success('Foto publicada no site');
      onSaved?.();
    } catch (e) {
      toast.error(e.message || 'Erro ao publicar');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch('/upload', { method: 'POST', body: fd });
      await save(res.url);
    } catch (e) {
      toast.error(e.message || 'Erro no upload');
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await apiFetch(`/media/overrides/${slot}`, { method: 'DELETE' });
      await refreshMediaOverrides();
      toast.success('Voltou à foto original');
      onSaved?.();
    } catch (e) {
      toast.error(e.message || 'Erro ao repor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`media-slot-${slot}`} className="overflow-hidden rounded-2xl border border-eco-ink/10 bg-white shadow-sm">
      <div className="relative h-36 bg-eco-ink/5">
        {current ? (
          <img src={current} alt={name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="grid h-full place-items-center text-eco-ink/30"><ImagePlus size={28} /></div>
        )}
        <span className={`absolute top-2 right-2 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${isCustom ? 'bg-eco-gold text-eco-deep' : 'bg-white/85 text-eco-ink/60'}`} data-testid={`media-slot-status-${slot}`}>
          {isCustom ? 'Personalizada' : 'Original'}
        </span>
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold text-eco-ink" title={name}>{name}</p>
          {(onMoveLeft || onMoveRight) && (
            <span className="flex shrink-0 gap-1">
              <button type="button" onClick={onMoveLeft} disabled={!onMoveLeft || busy} data-testid={`media-move-left-${slot}`} aria-label="Mover para a esquerda" className="grid h-7 w-7 place-items-center rounded-md border border-eco-ink/15 text-eco-ink/60 transition-colors hover:border-eco-gold hover:text-eco-gold disabled:opacity-30"><ArrowLeft size={13} /></button>
              <button type="button" onClick={onMoveRight} disabled={!onMoveRight || busy} data-testid={`media-move-right-${slot}`} aria-label="Mover para a direita" className="grid h-7 w-7 place-items-center rounded-md border border-eco-ink/15 text-eco-ink/60 transition-colors hover:border-eco-gold hover:text-eco-gold disabled:opacity-30"><ArrowRight size={13} /></button>
            </span>
          )}
        </div>
        <div className="mt-2.5 flex gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`media-file-input-${slot}`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            data-testid={`media-upload-${slot}`}
            className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-eco-ink/15 text-xs font-semibold text-eco-ink transition-colors hover:border-eco-green hover:text-eco-green disabled:opacity-40"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Carregar foto
          </button>
          {isCustom && (
            <button
              type="button"
              disabled={busy}
              onClick={revert}
              data-testid={`media-revert-${slot}`}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-eco-ink/15 px-3 text-xs font-semibold text-eco-ink/60 transition-colors hover:border-red-300 hover:text-red-500 disabled:opacity-40"
              title="Repor original"
            >
              <RotateCcw size={13} /> Repor
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminMedia = () => {
  const overrides = useMediaOverrides();
  const [query, setQuery] = useState('');
  const [transferSlots, setTransferSlots] = useState([]);
  const [experienceSlots, setExperienceSlots] = useState([]);
  const [cruiseSlots, setCruiseSlots] = useState([]);

  useEffect(() => {
    apiFetch('/admin/transfers')
      .then((d) => setTransferSlots((Array.isArray(d) ? d : []).map((tr) => ({ slot: `tr:${tr.slug}`, name: tr.name, img: tr.img }))))
      .catch(() => {});
    apiFetch('/admin/experiences')
      .then((d) => setExperienceSlots((Array.isArray(d) ? d : []).map((e) => ({ slot: `exp:${e.slug}`, name: e.name, img: e.img }))))
      .catch(() => {});
    apiFetch('/admin/shore-excursions')
      .then((data) => setCruiseSlots((Array.isArray(data) ? data : []).map((item) => ({ slot: `cruise:${item.slug}`, name: item.name, img: item.img }))))
      .catch(() => {});
  }, []);

  const allGroups = useMemo(() => {
    const dyn = [
      ...(experienceSlots.length > 0 ? [{ id: 'exp', label: 'Experiências', slots: experienceSlots }] : []),
      ...(cruiseSlots.length > 0 ? [{ id: 'cruise', label: 'Cruise & Shore Excursions', slots: cruiseSlots }] : []),
      ...(transferSlots.length > 0 ? [{ id: 'tr', label: 'Transfers', slots: transferSlots }] : []),
    ];
    return [GROUPS[0], ...dyn, ...GROUPS.slice(1)];
  }, [transferSlots, experienceSlots, cruiseSlots]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups.map((g) => ({ ...g, slots: g.slots.filter((s) => s.name.toLowerCase().includes(q)) })).filter((g) => g.slots.length > 0);
  }, [query, allGroups]);

  const total = allGroups.reduce((n, g) => n + g.slots.length, 0);
  const customCount = allGroups.reduce((n, g) => n + g.slots.filter((s) => overrides[s.slot]).length, 0);

  // Reorder hero carousel slides by swapping the effective image of two adjacent slots.
  // Only uploaded/media assets can be written as overrides (backend rejects external URLs).
  const swapHeroSlides = async (heroIndex, direction) => {
    const a = HERO_SLIDES[heroIndex];
    const b = HERO_SLIDES[heroIndex + direction];
    if (!a || !b) return;
    const imgA = overrides[a.slot] || a.img;
    const imgB = overrides[b.slot] || b.img;
    const local = (u) => u.startsWith('/api/files/') || u.startsWith('/media/');
    if (!local(imgA) || !local(imgB)) {
      toast.error('Reordenar exige fotos carregadas na Media Library em ambos os slides');
      return;
    }
    try {
      await apiFetch(`/media/overrides/${a.slot}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: imgB }) });
      await apiFetch(`/media/overrides/${b.slot}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: imgA }) });
      await refreshMediaOverrides();
      toast.success('Ordem do carrossel atualizada');
    } catch (e) {
      toast.error(e.message || 'Erro ao reordenar');
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-eco-ink/55">
          {customCount} de {total} fotos personalizadas — guardar publica imediatamente no site.
        </p>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-eco-ink/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Procurar foto…"
            className="eco-input w-56 !pl-8 !py-2 text-sm"
            data-testid="media-search"
          />
        </div>
      </div>
      {groups.map((g) => (
        <section key={g.id} className="mt-8">
          <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-eco-ink/45" data-testid={`media-group-${g.id}`}>{g.label} ({g.slots.length})</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {g.slots.map((s) => {
              const heroIdx = HERO_SLIDES.findIndex((h) => h.slot === s.slot);
              return (
                <MediaSlotCard
                  key={s.slot}
                  slot={s.slot}
                  name={s.name}
                  defaultImg={s.img}
                  overrides={overrides}
                  onMoveLeft={heroIdx > 0 ? () => swapHeroSlides(heroIdx, -1) : null}
                  onMoveRight={heroIdx >= 0 && heroIdx < HERO_SLIDES.length - 1 ? () => swapHeroSlides(heroIdx, 1) : null}
                />
              );
            })}
          </div>
        </section>
      ))}
      {groups.length === 0 && <p className="mt-8 text-center text-sm text-eco-ink/50" data-testid="media-empty">Nenhuma foto corresponde à pesquisa.</p>}
    </div>
  );
};
